import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog";
import { useTheme } from "@mui/material/styles";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import {
  CheckoutBillInterface,
  CheckoutBillProps,
} from "_interfaces/_common/payment";
import { formatPriceWithDelimiter } from "functions/helper";
import React, { Fragment, useState } from "react";

interface Props {
  checkoutBill: CheckoutBillInterface[];
}

interface RenderTableProps {
  checkoutBill: CheckoutBillInterface[];
  handleOpenDetailedBreakdown: (data: CheckoutBillProps[]) => void;
}

const PaymentCheckoutBillTable: React.FC<Props> = ({ checkoutBill }) => {
  const [detailedBreakdown, setDetailedBreakdown] = useState<
    CheckoutBillProps[] | null
  >(null);

  const handleOpenDetailedBreakdown = (data: CheckoutBillProps[]) =>
    setDetailedBreakdown(data);
  const handleCloseDetailedBreakdown = () => setDetailedBreakdown(null);

  return (
    <>
      <RenderTable
        checkoutBill={checkoutBill}
        handleOpenDetailedBreakdown={handleOpenDetailedBreakdown}
      />

      <Dialog
        open={Boolean(detailedBreakdown?.length)}
        onClose={handleCloseDetailedBreakdown}
        title="Detailed Breakdown"
      >
        {detailedBreakdown?.length ? (
          <RenderTable
            checkoutBill={detailedBreakdown}
            handleOpenDetailedBreakdown={handleOpenDetailedBreakdown}
          />
        ) : null}
      </Dialog>
    </>
  );
};

const RenderTable: React.FC<RenderTableProps> = ({
  checkoutBill,
  handleOpenDetailedBreakdown,
}) => {
  const { palette } = useTheme();

  return (
    <TableContainer>
      <Table>
        <TableBody>
          {checkoutBill.map((item, index, rows) => {
            const { cellProps = {}, labelProps = {}, valueProps = {} } = item;
            return (
              <Fragment key={index}>
                {item?.enabled ? (
                  <TableRow
                    key={index}
                    sx={{
                      ".MuiTableCell-root": {
                        py: 1,
                        borderBottom: index + 1 === rows?.length ? 0 : "",
                        px: 0,
                      },
                    }}
                  >
                    {item?.component ? (
                      <TableCell sx={{ ...cellProps }} colSpan={2}>
                        {item.component}
                      </TableCell>
                    ) : (
                      <>
                        <TableCell sx={{ fontSize: 14, ...cellProps }}>
                          <Typography sx={{ ...labelProps }} fontSize={14}>
                            {item.label}{" "}
                            {item?.breakdownDetails?.enabled &&
                            item?.breakdownDetails?.checkoutBill?.length ? (
                              <Box
                                component="span"
                                fontSize={12}
                                className="tw:cursor-pointer"
                                color={palette.primary.main}
                                onClick={() =>
                                  item?.breakdownDetails?.checkoutBill
                                    ?.length &&
                                  handleOpenDetailedBreakdown(
                                    item?.breakdownDetails?.checkoutBill
                                  )
                                }
                              >
                                View Breakdown
                              </Box>
                            ) : null}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ fontSize: 14, ...cellProps }}>
                          <Typography
                            sx={{
                              fontSize: 14,
                              fontWeight: 500,
                              textAlign: "right",
                              ...valueProps,
                            }}
                          >
                            {item?.component
                              ? item?.component
                              : item?.value && typeof item.value === "number"
                              ? formatPriceWithDelimiter(item.value, false)
                              : typeof item.value === "string"
                              ? item.value
                              : 0}
                          </Typography>
                        </TableCell>
                      </>
                    )}
                  </TableRow>
                ) : (
                  <></>
                )}
              </Fragment>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default PaymentCheckoutBillTable;
